import {
  type Component,
  Show,
  Suspense,
  createEffect,
  createMemo,
  createSignal,
  on,
} from "solid-js";
import { Dynamic, Portal } from "solid-js/web";

import { RouteTransitionWrapper } from "@/components/Base/Transition/Index";
import { getNextZIndex } from "@/components/Dialogs";
import { usePopStateHandler } from "@/hooks/usePopStateHandler";
import { useRouter } from "@/hooks/useRouter";
import { useValidRoute } from "@/hooks/useValidRoute.ts";
import { useWatch } from "@/hooks/useWatch";
import { overFlowFlagManager } from "@/lib/OverFlowFlag";
import { isMobile } from "@/lib/isMobile";
import { getRealPath } from "@/lib/language";
import { cn } from "@/lib/tailwindcssTool";
import IndexPage from "@/pages";
import {
  type ModalProps,
  checkModalPath,
  getModals,
} from "@/router/modalRoutes";
import { routersLeave } from "@/router/routersLeave";
import { routerStore, setRouterStore } from "@/stores/routerStore";
import { userStore } from "@/stores/userStore";
import { UserKycAuthActionType } from "@/types/compontent/base/baseEnum.ts";
import {
  Route,
  type RouteSectionProps,
  useBeforeLeave,
  useLocation,
} from "@solidjs/router";

import { InitApp } from "./InitApp";
import { DefaultLobbyLayout, layoutRoutes } from "./routes";

export interface RouteInfo {
  needLogin?: boolean;
  needKYC?: boolean;
  KYCAction?: number;
}

export const KYCRouterMapping = {
  deposit: UserKycAuthActionType.deposit,
  withdraw: UserKycAuthActionType.withdraw,
  game: UserKycAuthActionType.bet,
  gamedtail: UserKycAuthActionType.bet,
};

export interface RouteConfig {
  path: string;
  component: Component<RouteSectionProps>;
  children?: RouteConfig[];
  info?: RouteInfo;
}

const RootLayout = (props: RouteSectionProps) => {
  const location = useLocation();
  const { isValidRoute, handleNoLogin } = useValidRoute();
  usePopStateHandler();
  const {
    getFirstPathSegment,
    push,
    handleLoginBlackList,
    handleNotLoginBlackList,
  } = useRouter();
  const [isModalVisible, setIsModalVisible] = createSignal(false);
  const [delayedCurrentRoute, setDelayedCurrentRoute] = createSignal<
    string | null
  >(null);
  const [shouldStartExit, setShouldStartExit] = createSignal(false);
  const modals = getModals();
  // 动画完成后的清理函数
  const handleAnimationComplete = () => {
    setIsModalVisible(false);
    setDelayedCurrentRoute(null);
    setShouldStartExit(false);
  };
  createEffect(
    on(
      createMemo(() => routerStore.currentRoute || location.pathname),
      (pathname) => setRouterStore("currentFullPath", pathname),
    ),
  );
  // 判断为登陆状态时，路由弹窗是否黑名单
  useWatch(
    () => routerStore.currentRoute,
    () => {
      if (!handleLoginBlackList(routerStore.currentRoute || "")) {
        push("/");
        return;
      }
    },
  );
  // 判断为未登录状态时，路由弹窗是否黑名单
  useWatch(
    () => routerStore.currentRoute,
    () => {
      if (!handleNotLoginBlackList(routerStore.currentRoute || "")) {
        push("/");
        return;
      }
    },
  );
  useWatch(
    () => routerStore.currentRoute,
    (newVal, oldVal) => {
      if (oldVal && !newVal) {
        // 如果没有new值，有old值，则认为是关闭模态弹窗，需要移除标志量
        overFlowFlagManager.removeFlag();
      }
      if (newVal && !oldVal) {
        // 如果有new值，没有old值，则认为是打开第一个模态弹窗，需要添加标志量
        // 如果oldnew都有则认为是切换模态弹窗，不需要处理
        overFlowFlagManager.addFlag();
      }
    },
  );

  // 控制modal的显示和隐藏，支持退出动画
  createEffect(
    on(
      createMemo(() => routerStore.currentRoute),
      (currentRoute) => {
        const currentPath = getRealPath(location.pathname);
        const isCurrentModal = checkModalPath(currentPath);
        console.log("routerStore.currentRoute", currentRoute);
        if (currentRoute) {
          // 有modal路由时立即显示并更新延迟状态
          setIsModalVisible(true);
          setDelayedCurrentRoute(currentRoute);
          setShouldStartExit(false); // 重置退出状态
        } else if (
          !isCurrentModal &&
          delayedCurrentRoute() &&
          !shouldStartExit()
        ) {
          // 路由已经变化，当delayedCurrentRoute有值，开始退出动画
          setShouldStartExit(true);
        }
      },
    ),
  );

  // 路由拦截处理
  useBeforeLeave((e) => {
    // 如果目标路由需要登录但用户未登录
    if (!isValidRoute(typeof e.to === "string" ? e.to : "")) {
      e.preventDefault();
      handleNoLogin();
      return;
    }
  });
  // 统一添加路由拦截
  routersLeave();

  return (
    <InitApp>
      {props.children}
      <Portal mount={document.body}>
        <div
          class={`fixed inset-0 h-full w-full`}
          style={{
            visibility: isModalVisible() ? "visible" : "hidden",
            "z-index": getNextZIndex(),
          }}
        >
          {/* 底部遮罩 */}
          <div
            class={cn(
              "h-full w-full bg-black/70",
              !isMobile() && isModalVisible() && !shouldStartExit()
                ? "fade-in"
                : "fade-out",
            )}
          ></div>
          {/* 模态框 */}
          <Show when={isModalVisible()} keyed={false} fallback={<></>}>
            <div
              class={`fixed inset-0 h-full w-full`}
              style={{
                visibility: isModalVisible() ? "visible" : "hidden",
              }}
            >
              <RouteTransitionWrapper
                direction={isMobile() ? "right" : "center"}
                shouldExit={shouldStartExit()}
                onAnimationEnd={handleAnimationComplete}
              >
                <div class="flex h-full w-full items-center justify-center">
                  <Suspense>
                    <Dynamic
                      component={
                        modals[
                          getFirstPathSegment(
                            delayedCurrentRoute() ?? "",
                          ) as keyof typeof modals
                        ] as Component<ModalProps>
                      }
                      params={routerStore.modal.params}
                    />
                  </Suspense>
                </div>
              </RouteTransitionWrapper>
            </div>
          </Show>
        </div>
      </Portal>
    </InitApp>
  );
};

const getRoutes = (): RouteConfig[] => {
  const modals = getModals();
  return [
    {
      path: "/:lang?",
      component: RootLayout,
      children: [
        ...layoutRoutes(),
        ...Object.keys(modals).map((type) => ({
          path: type + "/*",
          component: (props: RouteSectionProps) => (
            <DefaultLobbyLayout {...props}>
              <Dynamic component={IndexPage} />
            </DefaultLobbyLayout>
          ),
        })),
      ],
    },
  ];
};

export function renderRoutes() {
  const renderRoute = (route: RouteConfig) => {
    const children = route.children?.map((child) => renderRoute(child)) || [];
    return (
      <Route path={route.path} component={route.component} info={route.info}>
        {children}
      </Route>
    );
  };

  return getRoutes().map((route) => renderRoute(route));
}
